import {
    ColDef,
    GridApi,
    RowNode,
    ValueFormatterParams,
    ValueGetterParams,
} from "ag-grid-community";

import {
    UpdateWithInputCellRenderer,
    StatusCellRenderer,
    UploadButtonCellRenderer,
    FilterTextFormatter,
} from "@cell-renderers/AllCellRenderers";
import {
    createCampaigns,
    getCampaignTarget,
    titleCase,
    inverse,
} from "./utils";
import { CAMPAIGN_TYPES, TARGET_TYPES } from "./constants";
import { showToast } from "./modals";
import $ from "jquery";
import moment from "moment";
import store from "../store";
import {
    ICampaignObj,
    ICustomCellRendererParams,
    IProcessedCampaigns,
    SelectOptions,
    TAdGroupObj,
    TCampaignObject,
    TCampaignTargetObject,
    TSelectedTargets,
    TargetArgs,
} from "./common-types";
import { TCommonRowData } from "./common-types";

export const campaignTargetsColDefs: ColDef[] = [
    {
        field: "tgt_title",
        headerName: "Target title",
        minWidth: 300,
        maxWidth: 400,
        filter: "agTextColumnFilter",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        tooltipField: "tgt_title",
        sortable: true,
        valueFormatter: (params: ValueFormatterParams) =>
            titleCase(params.value),
    },
    {
        field: "prod_target",
        headerName: "ASIN",
        minWidth: 300,
        maxWidth: 400,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params: ValueGetterParams) =>
            params.data.tgt_type === "product" ? params.data.prod_target : "",
    },
    {
        field: "program_type",
        sortable: true,
        headerName: "Program Type",
        minWidth: 200,
        filter: "agTextColumnFilter",
        tooltipField: "program_type",
        filterParams: {
            valueFormatter: FilterTextFormatter
        },
    },
    {
        field: "tgt_type",
        sortable: true,
        headerName: "Target type",
        minWidth: 200,
        filter: "agTextColumnFilter",
        tooltipField: "tgt_type",
        valueFormatter: (params: ValueFormatterParams) =>
            titleCase(params.value),
    },
    {
        field: "tgt_class",
        sortable: true,
        headerName: "Target class",
        minWidth: 200,
        filter: "agTextColumnFilter",
        tooltipField: "tgt_class",
        valueFormatter: (params: ValueFormatterParams) =>
            titleCase(params.value),
    },
    {
        field: "match_type",
        sortable: true,
        headerName: "Match Type",
        minWidth: 200,
        filter: "agTextColumnFilter",
        tooltipField: "match_type",

        valueFormatter: (params: ValueFormatterParams) =>
            titleCase(params.value === "asinSameAs" ? "" : params.value),
    },
    {
        field: "suggest_bid",
        sortable: true,
        // editable: true,
        headerName: "Recommended bid",
        maxWidth: 200,
        filter: "agNumberColumnFilter",
        tooltipField: "suggest_bid",
        cellRenderer: UpdateWithInputCellRenderer,

        cellRendererParams: {
            hasApplyButton: false,
            hasCancelButton: false,
            isThirdPartyRequestOnApply: false,
            origin: "campaign-creation-targets",
        },
    },
    {
        field: "bsr_rank",
        sortable: true,
        headerName: "BSR rank",
        minWidth: 200,
        filter: "agNumberColumnFilter",
    },
];

export const campaignsColDefs: ColDef[] = [
    {
        field: "name",
        headerName: "campaign name",
        minWidth: 300,
        maxWidth: 400,
        filter: "agTextColumnFilter",
        tooltipField: "name",
    },
    {
        field: "campaignType",
        headerName: "campaign type",
        minWidth: 300,
        maxWidth: 400,
        filter: "agTextColumnFilter",
        tooltipField: "campaignType",
    },
    {
        field: "totalTargetCount",
        headerName: "# Targets",
        minWidth: 200,
        maxWidth: 300,
        filter: "agNumberColumnFilter",
    },
    {
        field: "automate_bid",
        headerName: "Automate bid",
        maxWidth: 150,
        filter: false,
        cellRenderer: (params: ICustomCellRendererParams) =>
            StatusCellRenderer(params, "automate_bid"),
        cellRendererParams: {
            hasApplyButton: false,
            hasCancelButton: false,
            isThirdPartyRequestOnApply: false,
            isModalComponent: true,
            origin: "campaign-creation-automate-bid",
        }
    },
    {
        field: "dailyBudget",
        headerName: "daily budget",
        minWidth: 300,
        maxWidth: 400,
        filter: "agTextColumnFilter",
        cellRenderer: UpdateWithInputCellRenderer,

        cellRendererParams: {
            hasApplyButton: false,
            hasCancelButton: false,
            isThirdPartyRequestOnApply: false,
            origin: "campaign-creation",
        },
    },
    {
        field: "placementTop",
        headerName: "Placement top of search",
        minWidth: 200,
        maxWidth: 300,
        filter: "agNumberColumnFilter",

        cellRenderer: UpdateWithInputCellRenderer,
        cellRendererParams: {
            hasApplyButton: false,
            hasCancelButton: false,
            isThirdPartyRequestOnApply: false,
            origin: "campaign-creation",
            isDisabled: true,
        },
    },
    {
        field: "placementProductPage",
        headerName: "Placement product page",
        minWidth: 200,
        maxWidth: 300,
        filter: "agNumberColumnFilter",

        cellRenderer: UpdateWithInputCellRenderer,
        cellRendererParams: {
            hasApplyButton: false,
            hasCancelButton: false,
            isThirdPartyRequestOnApply: false,
            origin: "campaign-creation",
            isDisabled: true,
        },
    },
    {
        field: "headline",
        headerName: "Headline",
        minWidth: 400,
        maxWidth: 500,
        filter: "agTextColumnFilter",
        cellRenderer: UpdateWithInputCellRenderer,
        cellRendererParams: {
            hasApplyButton: false,
            hasCancelButton: false,
            isThirdPartyRequestOnApply: false,
            origin: "campaign-headline",
        },
    },
    {
        field: "",
        headerName: "Upload Custom Asset",
        equals: () => false,
        cellRenderer: UploadButtonCellRenderer,

        filter: false,
        maxWidth: 200,
        cellRendererParams: {
            origin: "campaign-custom-image",
        },
    },
    {
        field: "asset_name",
        headerName: "Asset Name",
        minWidth: 400,
        cellRendererParams: {
            // origin : "campaign-custom-image"
        },
    },
];

export function getTargetData(targetArgs: TargetArgs) {
    const {
        campaignSelected,
        portfolioSelected,
        targetSelected,
        targetClassSelected,
        matchTypeSelected,
        setTargetsRowData,
    } = targetArgs;

    const productsSelectionData = store.getState().productsSelectionData.value;

    const productAds = productsSelectionData.map(
        (option: { asin: string; state: string }) => ({
            asin: option.asin,
            state: "enabled",
        })
    );

    const campaignName = (
        document.getElementById("campaignname") as HTMLInputElement
    ).value;

    const portfolioId = portfolioSelected && portfolioSelected.value;

    const campaignType = campaignSelected.map(
        (option: SelectOptions) => CAMPAIGN_TYPES[option.value]
    );

    const landingPageAsins = productAds.map(
        ({ asin }: { asin: string }) => asin
    );

    const targetingType = targetSelected
        .map((option) => option.value)
        .filter((value) => value);

    const targetingClass = targetClassSelected.map((option) =>
        option.value.toLowerCase()
    );

    const matchType = matchTypeSelected
        .map((option: SelectOptions) => option.label)
        .filter((value) => value);

    const campaignObject: TCampaignTargetObject = {
        productAds: productAds,
        campaignName: campaignName,
        ...(portfolioId && { portfolioId: portfolioId }),
        campaignType: campaignType,
        targetingType: targetingType,
        targetingClass: targetingClass,
        ...(matchType.length && { matchType: matchType }),
    };

    if (campaignType.includes("sponsoredBrands")) {
        const brandName = (
            document.getElementById("brand_name") as HTMLInputElement
        ).value;
        campaignObject["brandName"] = brandName;
        campaignObject["landingPage"] = landingPageAsins;
    }

    window.sessionStorage.setItem(
        "campaignConfig",
        JSON.stringify(campaignObject)
    );
    // getCampaignTarget(campaignObject, campaignTargetsGridOptions);
    getCampaignTarget(campaignObject, setTargetsRowData);
}

type TSelected = { sku_title?: string; asin?: string };

export function appendProductsInformation(nameLength: number) {
    const selectedData = store.getState().productsSelectionData.value;
    const selectedProductDiv =
        document.getElementsByClassName("selectedProduct");
    selectedProductDiv[0].innerHTML = "";
    selectedData.forEach((s: TSelected) => {
        selectedProductDiv[0].innerHTML += `<span class="selected-filter badge border bg-light fw-normal text-dark ms-1 mt-1 position-relative">${(
            s.sku_title || s.asin
        ).substring(0, nameLength)}</span>`;
    });
}

function onCreateCampaignCickHandler() {
    $("#createCampaign").hide();
    $("#firstStep").hide();
    $("#back").show();
    $("#secondStep").show();
    $("#publish-campaigns").show();
    sessionStorage.removeItem("customAssetMapping");
}
export function onBackButtonClickHandler() {
    $("#createCampaign").show();
    $("#firstStep").show();
    $("#back").hide();
    $("#secondStep").hide();
    $("#publish-campaigns").hide();
}

export function resetCampaignCreationModal() {
    $("#campaign-config-form").find("input").val(null);
    onBackButtonClickHandler();
}

export function populateCreateCampaigns(
    campaignTargetsApi: GridApi,
    selectedNodes: RowNode[]
) {
    //- validation logic for campaign object fields
    const targets = Object.values(selectedNodes);
    const selectedTargets = targets.map((node: RowNode) => ({
        ...(node && node.data),
    }));
    if (Object.keys(selectedTargets).length === 0) {
        $("#createCampaign").css({
            opacity: "0.7",
            pointerEvents: "none",
        });
    } else {
        $("#createCampaign").css({
            opacity: "1",
            pointerEvents: "",
        });

        onCreateCampaignCickHandler();

        const campaignCreationConfig = JSON.parse(
            window.sessionStorage.getItem("campaignConfig")
        );

        const targetClassConstraint = {
            keyword: ["branded", "generic", "competition"],
            product: ["branded", "competition"],
            auto: ["auto"],
        };

        const campaigns = createCampaignForSelectedTargetProduct(
            campaignCreationConfig,
            targetClassConstraint
        );

        const processedCampaigns: Array<IProcessedCampaigns> = campaigns.map(
            (campaign: ICampaignObj) =>
                createAdgroup(campaign, campaignCreationConfig, selectedTargets)
        );

        const campaignsToDisplay: Array<TCampaignObject> = [];

        processedCampaigns.forEach((campaign: TCampaignObject) => {
            if (
                (campaign.campaignType === "sponsoredBrands" ||
                    campaign.campaignType === "sponsoredBrandsVideo") &&
                campaign.adGroups.length
            ) {
                campaignsToDisplay.push(campaign);
            } else {
                delete campaign["brandName"];
                campaignsToDisplay.push(campaign);
            }
        });

        showTargetCombinationHandler(campaignTargetsApi);
        displayCampaigns(
            campaignsToDisplay.filter(
                (c: TCampaignObject) =>
                    c.adGroups.reduce(
                        (acc: TAdGroupObj[], a: TAdGroupObj) => [
                            ...acc,
                            ...(a.keywords || a.targets || []),
                        ],
                        []
                    ).length
            ),
            campaignTargetsApi
        );
    }
}

export async function publishCampaigns(
    campaignApi: GridApi,
    handleClose: () => void
) {
    try {
        const campaigns: TCommonRowData = [];
        const headlines: Array<string> = [];
        const customAssetMap =
            sessionStorage.getItem("customAssetMapping") &&
            new Map(
                Object.entries(
                    JSON.parse(sessionStorage.getItem("customAssetMapping"))
                )
            );

        campaignApi.forEachNode((node) => {
            const data = node.data;
            delete data["totalTargetCount"];
            if (data.campaignType === "sponsoredBrands") {
                headlines.push(node.data.headline);

                const elementId = `#automate_bid-status-btn-${+node.rowIndex + 1
                    }`;

                const bidOptimizationStatus = $(elementId).prop("checked");

                data["bidOptimization"] =
                    bidOptimizationStatus === undefined
                        ? true
                        : bidOptimizationStatus;

                data["creative"] = {
                    ...(customAssetMap &&
                        customAssetMap.has(node.data.name) && {
                        customImageAssetId: customAssetMap.get(
                            node.data.name
                        ),
                    }),
                    brandName: node.data.brandName,
                    brandLogoAssetID: sessionStorage.getItem("strapiAssetID"),
                    headline: node.data.headline,
                    campaignType: data.campaignType,
                };

                delete data.brandName;
                delete data.headline;
                delete data.asset_name;
            } else if (data.campaignType === "sponsoredBrandsVideo") {
                if (
                    !customAssetMap ||
                    (!customAssetMap.has(node.data.name) &&
                        node.data.campaignType === "sponsoredBrandsVideo")
                ) {
                    throw new Error(
                        "Select or upload video for all sbv campaigns"
                    );
                }

                const elementId = `#automate_bid-status-btn-${+node.rowIndex + 1
                    }`;

                const bidOptimizationStatus = $(elementId).prop("checked");

                data["bidOptimization"] =
                    bidOptimizationStatus === undefined
                        ? true
                        : bidOptimizationStatus;

                data["creative"] = {
                    ...(customAssetMap &&
                        customAssetMap.has(node.data.name) && {
                        videoAssetIds: customAssetMap.get(node.data.name),
                    }),
                    campaignType: data.campaignType,
                };

                delete data.asset_name;
            }

            data["attribute"] = ["create_campaign"]
            delete data.isAutomateBidChecked;
            campaigns.push(data);
        });

        if (headlines.includes(undefined) || headlines.includes("")) {
            throw new Error("Enter headline for all campaigns");
        } else {
            $("#loader-backdrop").toggle();
            const response = await createCampaigns(campaigns);
            $("#loader-backdrop").toggle();
            handleClose();
            if (response.status) {
                showToast("success", response.message);
            } else {
                showToast("danger", response.message);
            }
        }
    } catch (err) {
        showToast("danger", (err as Error).message);
    }
}

function createCampaignForSelectedTargetProduct(
    productConfig: ICampaignObj,
    targetClassConstraint: Record<string, Array<string>>
) {
    const campaigns = [];
    for (const type of productConfig.campaignType) {
        for (const targetType of productConfig.targetingType) {
            // develop targetClassArray by taking target class from product config and taking care of target class constraint
            const availableTargetClass = productConfig.targetingClass;

            for (const className of availableTargetClass) {
                if (targetClassConstraint[targetType].includes(className)) {
                    // 1. Keep single "auto" if target type is auto as targetType and targetClass both are same
                    // 2. sponsoredBrands do not have Auto Targeting campaigns

                    if (
                        !(targetType === "auto" && type !== "sponsoredProducts")
                    ) {
                        const inversedCampaignType = inverse(CAMPAIGN_TYPES);
                        const name = `1DS | ${productConfig.campaignName
                            } | ${inversedCampaignType[
                                type
                            ].toUpperCase()}| ${titleCase(
                                TARGET_TYPES[targetType.toLowerCase()]
                            )}${className !== "auto"
                                ? ` | ${titleCase(className)}`
                                : ""
                            }`;
                        const campaignObject: ICampaignObj = {
                            name,
                            portfolioId: productConfig.portfolioId,
                            campaignType: type,
                            targetingType: targetType,
                            targetingClass: className,
                            state: "enabled",
                            startDate: moment().format("YYYY-MM-DD"),
                            productAds: productConfig.productAds,
                            dailyBudget: 0,
                        };

                        if (type === "sponsoredBrands") {
                            campaignObject["brandName"] =
                                productConfig.brandName;
                        }
                        campaigns.push(campaignObject);
                    }
                }
            }
        }
    }
    return campaigns;
}

function createAdgroup(
    campaign: ICampaignObj,
    productConfig: TCampaignTargetObject,
    selectedTargets: Array<TSelectedTargets>
) {
    const adGroups = [];
    let dailyBudget = 0;
    let totalTargetCount = 0;
    const campaignType: Record<string, string> = {
        sponsoredProducts: "sponsoredProducts",
        sponsoredBrands: "sponsoredBrands",
        sponsoredBrandsVideo: "sponsoredBrands",
    };
    //   * for "keywords" target type, use match type
    //    * for "product" or "auto" target type, don't use match type, so there will only be 1 object in adGroups array
    if (campaign.targetingType === "keyword") {
        // generate average budget
        let averageBudget = 0;

        for (const match of productConfig.matchType) {
            const adGroupObject: TAdGroupObj = {
                name: `${campaign.name} | ${match}`,
                state: "enabled",
                defaultBid: 1,
                keywords: [],
            };

            const keywords = selectedTargets.filter(
                (item) =>
                    item.tgt_type === campaign.targetingType &&
                    item.match_type.toLowerCase() === match.toLowerCase() &&
                    CAMPAIGN_TYPES[item.program_type.toLowerCase()] ===
                    campaignType[campaign.campaignType] &&
                    item.tgt_class === campaign.targetingClass
            );

            for (const keyword of keywords) {
                const keywordObject = {
                    keywordText: keyword.tgt_title, //* prod_target from db
                    state: "enabled",
                    matchType: keyword.match_type,
                    bid: +keyword.suggest_bid,
                };

                // increament average budget value by adding recommended_bid of all keywords for that campaign
                averageBudget = averageBudget + +keyword.suggest_bid;

                adGroupObject["keywords"].push(keywordObject);
            }

            if (keywords.length) {
                adGroups.push(adGroupObject);
            }
        }

        const totalNumberOfKeywords = adGroups.reduce(
            (acc, adGroup) => acc + adGroup.keywords.length,
            0
        );
        dailyBudget = (averageBudget / totalNumberOfKeywords) * 20; // 20 x average bid of associated keywords
        totalTargetCount = totalNumberOfKeywords;
    } else {
        // when targeting type is "product"
        const adGroupObject: TAdGroupObj = {
            name: `${campaign.name}`,
            state: "enabled",
            defaultBid: 1,
        };
        let averageBudget = 0;
        adGroupObject["targets"] = [];

        if (campaign.targetingType === "product") {
            const targets = selectedTargets.filter(
                (item) =>
                    item.tgt_type === campaign.targetingType &&
                    CAMPAIGN_TYPES[item.program_type.toLowerCase()] ===
                    campaign.campaignType &&
                    item.tgt_class === campaign.targetingClass
            );

            for (const target of targets) {
                const targetObject: TAdGroupObj = {
                    state: "enabled",
                    expressionType: "manual",
                    bid: +target.suggest_bid,
                    expression: [
                        {
                            value: target.prod_target, //* prod_target  from db
                            type: "asinSameAs",
                        },
                    ],
                };
                // increment average budget value by adding bid values of all targets for that campaign
                averageBudget = averageBudget + +target.suggest_bid;
                adGroupObject["targets"].push(targetObject);
            }
        } else {
            // when targeting type is "auto"
            const targets = selectedTargets.filter(
                (item) => item.tgt_type === campaign.targetingType
            );
            for (const target of targets) {
                const targetObject: TAdGroupObj = {
                    state: "enabled",
                    expressionType: target.tgt_type,
                    bid: +target.suggest_bid,
                    expression: [
                        {
                            type: target.tgt_class,
                        },
                    ],
                };
                // increment average budget value by adding bid values of all targets for that campaign
                averageBudget = averageBudget + +target.suggest_bid;
                adGroupObject["targets"].push(targetObject);
            }
        }
        adGroups.push(adGroupObject);

        const totalNumberOfTargets = adGroups.reduce(
            (acc, adGroup) => acc + adGroup.targets.length,
            0
        );

        dailyBudget = (averageBudget / totalNumberOfTargets) * 20; // 20 x average bid of associated targets
        totalTargetCount = totalNumberOfTargets;
    }

    return {
        ...campaign,
        adGroups,
        dailyBudget: Math.ceil(dailyBudget),
        totalTargetCount,
        placementTop: 25,
        placementProductPage: 20,
        isAutomateBidChecked: true,
    };
}

function showTargetCombinationHandler(campaignTargetsApi: GridApi) {
    campaignTargetsApi.setRowData([]);
}

function displayCampaigns(
    campaigns: Array<TCampaignObject>,
    campaignTargetsApi: GridApi
) {
    campaignTargetsApi.setRowData(campaigns);
}
